import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Close } from "@air/next-icons";
import { Button } from "@air/primitive-button";
import { IconButton } from "@air/primitive-icon-button";
import { Modal, ModalCloseButton, ModalTitle } from "@air/primitive-modal";
import { useToasts } from "@air/provider-toast";
import pluralize from "pluralize";
import React, { useCallback, useState } from "react";
import { PrivateBoardSearch } from "~/components/BoardSearch/PrivateBoardSearch";
import BoardThumbnail from "~/components/BoardThumbnail/BoardThumbnail";
import { ListSectionHeader } from "~/components/Shared/ListSectionHeader";
import { ToastLink } from "~/components/UI/ToastLink";
import { ADD_CLIPS_TO_BOARD_MODAL } from "~/constants/testIDs";
import { useBoardPermissionsCache } from "~/hooks/useBoardPermissionsCache";
import { useGoToBoardPage } from "~/hooks/useGoToBoardPage";
import { useShowSubscriptionExpiredModal } from "~/hooks/useShowSubscriptionExpiredModal";
import { useAddAssetsToBoards } from "~/swr-hooks/boards/useAddAssetsToBoards";
import { canAddAssetsToBoard } from "~/utils/permissions/boardPermissions";
const getInstructions = (isPlural)=>isPlural ? "The assets will appear on a new board (or boards) and will not be removed from their current locations." : "The asset will appear on a new board (or boards) and will not be removed from its current location.";
export const AddAssetsToBoardsModal = (param)=>{
    let { clipIds, onClose, trackLocation, onSubmit, parentBoardId } = param;
    const { goToBoardPage } = useGoToBoardPage();
    const { showToast } = useToasts();
    const [adding, setAdding] = useState(false);
    const [showSummary, setShowSummary] = useState(false);
    const [selectedBoards, setSelectedBoards] = useState([]);
    const isBoardSelected = useCallback((board)=>{
        return selectedBoards.some((b)=>b.id === board.id);
    }, [
        selectedBoards
    ]);
    const { getBoardPermissions } = useBoardPermissionsCache();
    const { showingSubscriptionExpiredModal } = useShowSubscriptionExpiredModal({
        onClose
    });
    const addAssetsToBoardMutation = useAddAssetsToBoards();
    const onBoardSelectChange = useCallback((param)=>{
        let { board, selected } = param;
        setSelectedBoards((current)=>{
            if (selected) {
                return [
                    ...current,
                    board
                ];
            } else {
                return current.filter((b)=>b.id !== board.id);
            }
        });
    }, []);
    const getIsBoardDisabled = useCallback((board)=>{
        const boardPermissions = getBoardPermissions(board.id);
        if (!canAddAssetsToBoard(boardPermissions)) {
            return {
                isDisabled: true,
                message: "You do not have permission to add assets to this board"
            };
        }
    }, [
        getBoardPermissions
    ]);
    if (showingSubscriptionExpiredModal) {
        return null;
    }
    const goToBoard = (board)=>{
        goToBoardPage({
            board,
            trackLocation: "add-clips-to-board-toast"
        });
    };
    const handleAddClick = async ()=>{
        const boards = selectedBoards;
        setAdding(true);
        try {
            await addAssetsToBoardMutation.mutateAsync({
                clipIds,
                boards,
                trackLocation,
                parentBoardId
            });
            onSubmit === null || onSubmit === void 0 ? void 0 : onSubmit();
            onClose();
            showToast(/*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    "".concat(pluralize("item", clipIds.length, true), " added to "),
                    boards.length === 1 ? /*#__PURE__*/ _jsx(ToastLink, {
                        onClick: ()=>goToBoard(boards[0]),
                        children: boards[0].title
                    }) : /*#__PURE__*/ _jsx(_Fragment, {
                        children: "multiple boards"
                    })
                ]
            }));
        } catch (_error) {
            setAdding(false);
            showToast("An error occurred - please try again later");
        }
    };
    return /*#__PURE__*/ _jsx(Modal, {
        isOpen: true,
        onDismiss: onClose,
        "data-testid": ADD_CLIPS_TO_BOARD_MODAL,
        children: /*#__PURE__*/ _jsxs("div", {
            className: "flex flex-col gap-4",
            children: [
                /*#__PURE__*/ _jsxs("header", {
                    className: "flex justify-between gap-4",
                    children: [
                        /*#__PURE__*/ _jsx(ModalTitle, {
                            children: "Add to…"
                        }),
                        /*#__PURE__*/ _jsx(ModalCloseButton, {
                            className: "shrink-0",
                            onClick: onClose
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("p", {
                    className: "text-14 text-grey-10",
                    children: getInstructions(clipIds.length > 1)
                }),
                /*#__PURE__*/ _jsx(PrivateBoardSearch, {
                    className: "mt-3 [&>.workspace-boards]:h-[240px]",
                    isBoardSelected: isBoardSelected,
                    onBoardSelectChange: onBoardSelectChange,
                    shouldExpandLibraryInsteadOfSelect: true,
                    getIsBoardDisabled: getIsBoardDisabled
                }),
                /*#__PURE__*/ _jsx(ListSectionHeader, {
                    className: "mb-0 pb-0",
                    showDowntick: "always",
                    onClick: ()=>setShowSummary(!showSummary),
                    collapsed: !showSummary,
                    headerTitle: /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            "Selected boards (",
                            selectedBoards.length,
                            ")"
                        ]
                    })
                }),
                showSummary && /*#__PURE__*/ _jsx("div", {
                    className: "flex flex-col gap-2",
                    children: selectedBoards.map((option, index)=>{
                        var _option_ancestors;
                        return /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center justify-between rounded bg-grey-3 px-3 py-2",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex items-center gap-2",
                                    children: [
                                        /*#__PURE__*/ _jsx(BoardThumbnail, {
                                            board: option,
                                            sizes: "32px",
                                            width: 32,
                                            height: 32
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            children: [
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: "text-14 font-medium text-grey-11",
                                                    children: option.title
                                                }),
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: "text-12 text-grey-10",
                                                    children: ((_option_ancestors = option.ancestors) === null || _option_ancestors === void 0 ? void 0 : _option_ancestors.map((a)=>a.title).join(" / ")) || "Workspace Boards"
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(IconButton, {
                                    appearance: "ghost",
                                    color: "grey",
                                    size: "extra-small",
                                    icon: Close,
                                    label: "Clear",
                                    onClick: ()=>setSelectedBoards((boards)=>boards.filter((b)=>b.id !== option.id))
                                })
                            ]
                        }, index);
                    })
                }),
                /*#__PURE__*/ _jsxs("footer", {
                    className: "flex justify-end gap-3",
                    children: [
                        /*#__PURE__*/ _jsx(Button, {
                            appearance: "ghost",
                            color: "grey",
                            onClick: onClose,
                            size: "large",
                            children: "Cancel"
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            "data-testid": "ADD_CLIPS_TO_BOARD_MODAL_SUBMIT",
                            onClick: handleAddClick,
                            isLoading: adding,
                            disabled: adding || !selectedBoards.length,
                            appearance: "filled",
                            color: "blue",
                            size: "large",
                            children: adding ? "Adding…" : "Add to ".concat(selectedBoards.length >= 1 ? pluralize("board", selectedBoards.length, true) : "board")
                        })
                    ]
                })
            ]
        })
    });
};
